import React, { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { CalendarOff, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { StepHeader } from "@/components/intake/Primitives";
import { AvailabilityDayPicker } from "@/components/dashboard/AvailabilityDayPicker";

const DURATIONS = [30, 45, 60, 90, 120];
const CUSTOM = "custom";

/**
 * Final step of the supervisor-filled intake: choose a therapist and one of
 * their genuinely free slots. Slots come from the therapist's availability with
 * booked times already marked, so a clash cannot be selected in the first place.
 */
export function SupervisorAssignStep({ value, onChange, stepNumber, service }) {
  const { data: directory } = useQuery({
    queryKey: ["directory"],
    queryFn: async () => (await api.get("/users/directory")).data.items,
  });
  const { data: meta } = useQuery({
    queryKey: ["meta"],
    queryFn: async () => (await api.get("/submissions/meta")).data,
    staleTime: Infinity,
  });

  const therapists = useMemo(
    () => (directory || []).filter((u) => u.status === "active"),
    [directory]
  );

  const { data: slotData, isFetching } = useQuery({
    queryKey: ["availability-slots", value.admin_id, value.date, value.duration_minutes],
    queryFn: async () =>
      (
        await api.get(`/availability/${value.admin_id}/slots`, {
          params: { date: value.date, duration_minutes: value.duration_minutes },
        })
      ).data,
    enabled: !!value.admin_id && !!value.date,
  });

  const slots = slotData?.slots || [];
  const hasHours = (slotData?.blocks || []).length > 0;
  const chosen = therapists.find((t) => t.id === value.admin_id);

  // Clear a selected time that is no longer free after any change above it.
  useEffect(() => {
    if (value.start && slotData && !slots.some((s) => s.start === value.start && s.available)) {
      onChange({ start: "" });
    }
  }, [slotData]); // eslint-disable-line react-hooks/exhaustive-deps

  const specialtyOf = (u) => u.specialty && meta?.specialties?.[u.specialty];
  // Therapists whose specialty matches the requested service come first.
  const preferred = meta?.service_to_specialty?.[service];

  const sorted = useMemo(() => {
    if (!preferred) return therapists;
    return [...therapists].sort((a, b) => {
      const am = a.specialty === preferred ? 0 : 1;
      const bm = b.specialty === preferred ? 0 : 1;
      return am - bm || (a.name || "").localeCompare(b.name || "");
    });
  }, [therapists, preferred]);

  return (
    <div className="step-enter" data-testid="supervisor-assign-step">
      <StepHeader
        number={stepNumber}
        title="Assign a therapist"
        subtitle="Optional. Pick a free slot now, or leave this blank and schedule later from the dashboard."
      />

      <div className="emerge-card px-4 py-4 sm:px-5">
        <label htmlFor="sv-admin" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Therapist
        </label>
        <select
          id="sv-admin"
          className="emerge-input"
          value={value.admin_id || ""}
          onChange={(e) => onChange({ admin_id: e.target.value, start: "" })}
          data-testid="supervisor-assign-therapist"
        >
          <option value="">Not assigned yet</option>
          {sorted.map((u) => {
            const spec = specialtyOf(u);
            return (
              <option key={u.id} value={u.id}>
                {u.name}
                {spec ? ` - ${spec.label}` : ""}
                {preferred && u.specialty === preferred ? "  (matches request)" : ""}
              </option>
            );
          })}
        </select>

        {value.admin_id && (
          <>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <div>
                <label htmlFor="sv-duration" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Duration
                </label>
                <select
                  id="sv-duration"
                  className="emerge-input"
                  value={value.durationMode ?? value.duration_minutes}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === CUSTOM) onChange({ durationMode: CUSTOM, start: "" });
                    else onChange({ durationMode: Number(v), duration_minutes: Number(v), start: "" });
                  }}
                  data-testid="supervisor-assign-duration"
                >
                  {DURATIONS.map((d) => (
                    <option key={d} value={d}>{d} minutes</option>
                  ))}
                  <option value={CUSTOM}>Custom...</option>
                </select>
              </div>
              {value.durationMode === CUSTOM && (
                <div>
                  <label htmlFor="sv-duration-custom" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Custom duration (minutes)
                  </label>
                  <input
                    id="sv-duration-custom"
                    type="number"
                    min={15}
                    max={480}
                    step={5}
                    className="emerge-input"
                    style={{ direction: "ltr" }}
                    value={value.duration_minutes}
                    onChange={(e) => onChange({ duration_minutes: Number(e.target.value), start: "" })}
                    data-testid="supervisor-assign-duration-custom"
                  />
                </div>
              )}
            </div>

            <div className="mt-4">
              <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Date
              </span>
              <AvailabilityDayPicker
                adminId={value.admin_id}
                value={value.date}
                onChange={(d) => onChange({ date: d, start: "" })}
                durationMinutes={Number(value.duration_minutes) || 45}
              />
            </div>

            <div className="mt-4">
              <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Available times
              </span>
              {!value.date ? (
                <p className="rounded-[10px] border border-dashed border-border px-4 py-3 text-sm text-muted-foreground">
                  Choose a date.
                </p>
              ) : isFetching ? (
                <p className="flex items-center gap-2 py-3 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Checking availability...
                </p>
              ) : !hasHours ? (
                <p className="flex items-start gap-2 rounded-[10px] border border-[hsl(46_55%_80%)] bg-[hsl(46_90%_94%)] px-4 py-3 text-sm text-[hsl(30_70%_25%)]">
                  <CalendarOff className="mt-0.5 h-4 w-4 shrink-0" />
                  {chosen?.name || "This therapist"} has no available hours on that date.
                </p>
              ) : (
                <div className="grid grid-cols-3 gap-2 sm:grid-cols-4" data-testid="supervisor-assign-slots">
                  {slots.map((s) => {
                    const selected = s.start === value.start;
                    return (
                      <button
                        key={s.start}
                        type="button"
                        disabled={!s.available}
                        onClick={() => onChange({ start: s.start })}
                        title={s.available ? `Book ${s.local}` : `${s.local} is already booked`}
                        className={[
                          "rounded-[8px] border px-2 py-2 text-sm font-semibold transition",
                          selected
                            ? "border-primary bg-primary text-white"
                            : s.available
                              ? "border-border bg-white text-foreground hover:border-primary hover:text-primary"
                              : "cursor-not-allowed border-border bg-secondary/60 text-muted-foreground line-through",
                        ].join(" ")}
                      >
                        {s.local}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <label className="mt-4 flex items-start gap-3 rounded-[10px] border border-border bg-secondary/50 px-4 py-3">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 accent-[#2f5d83]"
                checked={value.confirmed !== false}
                onChange={(e) => onChange({ confirmed: e.target.checked })}
                data-testid="supervisor-assign-confirmed"
              />
              <span className="text-sm">
                <span className="font-semibold text-foreground">Confirmed appointment</span>
                <span className="block text-xs text-muted-foreground">
                  Untick to hold the slot but leave it on the waiting list until it is confirmed.
                </span>
              </span>
            </label>

            <div className="mt-4">
              <label htmlFor="sv-type" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Appointment type
              </label>
              <select
                id="sv-type"
                className="emerge-input"
                value={value.type}
                onChange={(e) => onChange({ type: e.target.value })}
                data-testid="supervisor-assign-type"
              >
                {Object.entries(meta?.appointment_types || {}).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
